import React, { Component } from "react";
import ReactTable from "react-table";
import moment from "moment";

import Backend from "../../../../utils/Backend";
import Notify from "../../../../utils/Notify";
import General from "../../../../utils/General";

import EditStaffModal from "../../modals/EditStaffModal";
import AddNewCompanyMemberModal from "../../modals/AddNewCompanyMemberModal";

export default class ManageStaffTable extends Component {
  constructor(props) {
    super(props);

    this.table = React.createRef();

    this.state = {
      data: [],
      pages: 0,
      loading: true,
      searchText: "",
      selectedStaff: null,
      showEditModal: false,
      showAddModal: false,
    };
  }

  componentDidMount() {
    this._loadData();
  }

  async _loadData() {
    try {
      this.setState({ loading: true });

      const companyId = this.props.vendorId;
      const venueId = this.props.venueId;

      if (!companyId) {
        Notify.error("Vendor ID is required");
        this.setState({ loading: false });
        return;
      }

      // Fetch staff for this venue
      const staffList = await Backend.getStaff(companyId, venueId);

      this.setState({
        data: staffList || [],
        loading: false,
      });
    } catch (error) {
      console.error("Failed to load staff:", error);
      Notify.error(error.message || "Failed to load staff data");
      this.setState({ loading: false });
    }
  }

  _handleEdit(staff) {
    this.setState({
      selectedStaff: staff,
      showEditModal: true,
    });
  }

  _handleDelete(staff) {
    const staffName = `${staff?.company_member?.user?.first_name || ""} ${
      staff?.company_member?.user?.last_name || ""
    }`.trim();

    const confirmDelete = window.confirm(
      `Are you sure you want to remove ${staffName} from this store?\n\nThis action cannot be undone.`
    );

    if (confirmDelete) {
      this._deleteStaff(staff);
    }
  }

  async _deleteStaff(staff) {
    try {
      this.setState({ loading: true });

      const companyMemberId = staff?.company_member?.user?.id;
      const venueId = this.props.venueId;

      await Backend.removeStaff(companyMemberId, venueId);

      Notify.success("Staff member removed successfully");
      this._loadData();
    } catch (error) {
      console.error("Delete failed:", error);
      Notify.error(error.message || "Failed to remove staff member");
      this.setState({ loading: false });
    }
  }

  _handleSearch(searchText) {
    this.setState({ searchText });
  }

  _filterData() {
    const { data, searchText } = this.state;

    if (!searchText) return data;

    const search = searchText.toLowerCase();

    return data.filter((staff) => {
      const firstName =
        staff?.company_member?.user?.first_name?.toLowerCase() || "";
      const lastName =
        staff?.company_member?.user?.last_name?.toLowerCase() || "";
      const email = staff?.company_member?.user?.email?.toLowerCase() || "";
      const phone = staff?.company_member?.user?.phone_number || "";
      const role = staff?.company_member?.role?.toLowerCase() || "";

      return (
        firstName.includes(search) ||
        lastName.includes(search) ||
        email.includes(search) ||
        phone.includes(search) ||
        role.includes(search)
      );
    });
  }

  render() {
    const { loading, showEditModal, selectedStaff, showAddModal, searchText } =
      this.state;

    const filteredData = this._filterData();

    const columns = [
      {
        Header: "STAFF MEMBER",
        accessor: "company_member.user",
        Cell: ({ value }) => {
          const firstName = value?.first_name || "";
          const lastName = value?.last_name || "";
          const initial = firstName.charAt(0).toUpperCase();
          return (
            <div className="d-flex align-items-center">
              <div className="symbol symbol-40 symbol-circle symbol-light-success mr-4">
                <span className="symbol-label font-size-h5 font-weight-boldest">
                  {initial}
                </span>
              </div>
              <div>
                <span className="text-dark font-weight-bolder d-block font-size-lg">
                  {General.toTitleCase(firstName)} {General.toTitleCase(lastName)}
                </span>
              </div>
            </div>
          );
        },
        sortable: true,
        minWidth: 220,
      },
      {
        Header: "EMAIL",
        accessor: "company_member.user.email",
        Cell: ({ value }) => (
          <span className="text-muted font-weight-bold d-block font-size-sm">
            {value || "-"}
          </span>
        ),
        sortable: true,
        minWidth: 200,
      },
      {
        Header: "PHONE",
        accessor: "company_member.user.phone_number",
        Cell: ({ value }) => (
          <span className="text-dark-75 font-weight-bolder d-block font-size-sm">
            {value || "-"}
          </span>
        ),
        sortable: true,
        width: 140,
      },
      {
        Header: "ROLE",
        accessor: "company_member.role",
        Cell: ({ value }) => (
          <span className="label label-light-primary label-inline font-weight-bolder">
            {General.toTitleCase(value || "Staff")}
          </span>
        ),
        sortable: true,
        width: 100,
      },
      {
        Header: "STATUS",
        accessor: "company_member.user.is_active",
        Cell: ({ value }) => {
          if (value === true || value === undefined) {
            return (
              <span className="label label-success label-inline font-weight-bold">
                Active
              </span>
            );
          }
          return (
            <span className="label label-danger label-inline font-weight-bold">
              Inactive
            </span>
          );
        },
        sortable: true,
        width: 100,
      },
      {
        Header: "JOINED",
        accessor: "created_at",
        Cell: ({ value }) => {
          return value ? (
            <span className="text-muted font-weight-bold font-size-sm">
              {moment(value).format("DD MMM YYYY")}
            </span>
          ) : "-";
        },
        sortable: true,
        width: 120,
      },
      {
        Header: "ACTIONS",
        accessor: "id",
        Cell: ({ original }) => (
          <div className="text-right pr-3">
            <button
              className="btn btn-icon btn-light btn-sm mr-2"
              onClick={() => this._handleEdit(original)}
              title="Edit Staff"
            >
              <i className="flaticon2-pen text-primary icon-sm"></i>
            </button>
            <button
              className="btn btn-icon btn-light btn-sm"
              onClick={() => this._handleDelete(original)}
              title="Remove Staff"
            >
              <i className="flaticon2-trash text-danger icon-sm"></i>
            </button>
          </div>
        ),
        sortable: false,
        width: 120,
      },
    ];

    return (
      <div className="card card-custom gutter-b">
        <div className="card-header border-0 pt-5">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label font-weight-bolder text-dark">
              Staff Management
            </span>
            <span className="text-muted mt-2 font-weight-bold font-size-sm">
              Total {filteredData.length} staff member{filteredData.length !== 1 ? 's' : ''}
            </span>
          </h3>
          <div className="card-toolbar">
            <button
              className="btn btn-primary font-weight-bolder font-size-sm"
              onClick={() => this.setState({ showAddModal: true })}
            >
              <i className="flaticon2-plus"></i>
              Add New Staff
            </button>
          </div>
        </div>

        <div className="card-body pt-3 pb-5">
          {/* Search and Filter Bar */}
          <div className="d-flex align-items-center mb-5">
            <div className="input-icon input-icon-right flex-grow-1 mr-3" style={{ maxWidth: '350px' }}>
              <input
                type="text"
                className="form-control form-control-solid"
                placeholder="Search staff..."
                value={searchText}
                onChange={(e) => this._handleSearch(e.target.value)}
              />
              <span>
                <i className="flaticon2-search-1 icon-md"></i>
              </span>
            </div>
            <button
              className="btn btn-light-primary font-weight-bold btn-sm"
              onClick={() => this._loadData()}
            >
              <i className="flaticon2-reload-1 icon-sm"></i>
            </button>
          </div>

          {/* Staff Table */}
          <div style={{ overflow: 'visible' }}>
            <ReactTable
              data={filteredData}
              columns={columns}
              loading={loading}
              defaultPageSize={10}
              className="-highlight"
              noDataText={
                loading ? "Loading..." : "No staff members found"
              }
              showPagination={filteredData.length > 10}
              minRows={0}
              getTableProps={() => ({
                className: 'table table-head-custom table-vertical-center',
                style: {
                  borderCollapse: 'separate',
                  borderSpacing: '0 0.75rem'
                }
              })}
              getTheadProps={() => ({
                style: {
                  display: 'none'
                }
              })}
              getTheadThProps={() => ({
                style: {
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#B5B5C3',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  padding: '1rem 1.5rem',
                  backgroundColor: 'transparent',
                  border: 'none'
                }
              })}
              getTrProps={() => ({
                style: {
                  backgroundColor: '#F3F6F9',
                  borderRadius: '0.42rem',
                  marginBottom: '0.75rem'
                }
              })}
              getTdProps={() => ({
                style: {
                  padding: '1.25rem 1.5rem',
                  border: 'none',
                  verticalAlign: 'middle',
                  backgroundColor: '#F3F6F9'
                }
              })}
              getTrGroupProps={() => ({
                style: {
                  marginBottom: '0.75rem'
                }
              })}
            />
          </div>
        </div>

        {/* Edit Staff Modal */}
        {showEditModal && selectedStaff && (
          <EditStaffModal
            show={showEditModal}
            staff={selectedStaff}
            vendorId={this.props.vendorId}
            venueId={this.props.venueId}
            onUpdated={() => {
              this.setState({ showEditModal: false, selectedStaff: null });
              this._loadData();
            }}
            onHide={() =>
              this.setState({ showEditModal: false, selectedStaff: null })
            }
          />
        )}

        {/* Add New Staff Modal */}
        {showAddModal && (
          <AddNewCompanyMemberModal
            show={showAddModal}
            vendorId={this.props.vendorId}
            venueId={this.props.venueId}
            onUpdated={() => {
              this.setState({ showAddModal: false });
              this._loadData();
            }}
            onHide={() => this.setState({ showAddModal: false })}
          />
        )}
      </div>
    );
  }
}
